// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
// eslint-disable-next-line import/no-extraneous-dependencies
import { MenuInfo } from 'rc-menu/lib/interface';

import { CombinedState } from 'reducers';
import AnnotationMenuComponent, { Actions } from 'components/annotation-page/top-bar/annotation-menu';
import { updateJobAsync } from 'actions/jobs-actions';
import {
    saveAnnotationsAsync,
    setForceExitAnnotationFlag as setForceExitAnnotationFlagAction,
    removeAnnotationsAsync as removeAnnotationsAsyncAction,
} from 'actions/annotation-actions';
import { exportActions } from 'actions/export-actions';
import { importActions } from 'actions/import-actions';
import {
    getCore, Job, JobStage, JobState,
} from 'cvat-core-wrapper';
import { message } from 'antd';
import { t } from 'utils/i18n';

const core = getCore();

interface StateToProps {
    jobInstance: Job;
    stopFrame: number;
}

interface DispatchToProps {
    showExportModal: (jobInstance: Job) => void;
    showExportBlenderModal: (jobInstance: Job) => void;
    showImportModal: (jobInstance: Job, isReference?: boolean) => void;
    removeAnnotations(startnumber: number, endnumber: number, delTrackKeyframesOnly: boolean): void;
    setForceExitAnnotationFlag(forceExit: boolean): void;
    saveAnnotations(afterSave?: () => void): void;
    updateJob(jobInstance: Job): Promise<boolean>;
}

function mapStateToProps(state: CombinedState): StateToProps {
    const {
        annotation: {
            job: {
                instance: jobInstance,
                instance: { stopFrame },
            },
        },
    } = state;

    return {
        jobInstance,
        stopFrame,
    };
}

function mapDispatchToProps(dispatch: any): DispatchToProps {
    return {
        showExportModal(jobInstance: Job): void {
            dispatch(exportActions.openExportDatasetModal(jobInstance));
        },
        showExportBlenderModal(jobInstance: Job): void {
            dispatch(exportActions.openExportBlenderModal(jobInstance));
        },
        showImportModal(jobInstance: Job, isReference = false): void {
            dispatch(importActions.openImportDatasetModal(jobInstance, isReference));
        },
        removeAnnotations(startnumber: number, endnumber: number, delTrackKeyframesOnly:boolean) {
            dispatch(removeAnnotationsAsyncAction(startnumber, endnumber, delTrackKeyframesOnly));
        },
        setForceExitAnnotationFlag(forceExit: boolean): void {
            dispatch(setForceExitAnnotationFlagAction(forceExit));
        },
        saveAnnotations(afterSave?: () => void): void {
            dispatch(saveAnnotationsAsync(afterSave));
        },
        updateJob(jobInstance: Job): Promise<boolean> {
            return dispatch(updateJobAsync(jobInstance));
        },
    };
}

type Props = StateToProps & DispatchToProps & RouteComponentProps;

function AnnotationMenuContainer(props: Props): JSX.Element {
    const {
        jobInstance,
        stopFrame,
        history,
        showExportModal,
        showExportBlenderModal,
        showImportModal,
        removeAnnotations,
        setForceExitAnnotationFlag,
        saveAnnotations,
        updateJob,
    } = props;

    const onClickMenu = (params: MenuInfo): void => {
        const [action] = params.keyPath;
        if (action === Actions.EXPORT_JOB_DATASET) {
            showExportModal(jobInstance);
        } else if (action === Actions.EXPORT_JOB_BLENDER) {
            showExportBlenderModal(jobInstance);
        } else if (action === Actions.RENEW_JOB) {
            jobInstance.state = core.enums.JobState.NEW;
            jobInstance.stage = JobStage.ANNOTATION;
            updateJob(jobInstance).then((success) => {
                if (success) {
                    message.info(t('Job renewed'), 2);
                }
            });
        } else if (action === Actions.FINISH_JOB) {
            jobInstance.stage = JobStage.ACCEPTANCE;
            jobInstance.state = core.enums.JobState.COMPLETED;
            updateJob(jobInstance).then((success) => {
                if (success) {
                    history.push(`/tasks/${jobInstance.taskId}`);
                }
            });
        } else if (action === Actions.OPEN_TASK) {
            history.push(`/tasks/${jobInstance.taskId}`);
        } else if (action.startsWith('state:')) {
            [, jobInstance.state] = action.split(':') as [string, JobState];
            updateJob(jobInstance).then((success) => {
                if (success) {
                    message.info(t('Job state updated'), 2);
                }
            });
        } else if (action === Actions.LOAD_JOB_ANNO) {
            showImportModal(jobInstance);
        } else if (action === Actions.LOAD_JOB_REFERENCE) {
            showImportModal(jobInstance, true);
        }
    };

    return (
        <AnnotationMenuComponent
            taskMode={jobInstance.mode}
            onClickMenu={onClickMenu}
            removeAnnotations={removeAnnotations}
            setForceExitAnnotationFlag={setForceExitAnnotationFlag}
            saveAnnotations={saveAnnotations}
            jobInstance={jobInstance}
            stopFrame={stopFrame}
        />
    );
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AnnotationMenuContainer));
